import React, { useState, useEffect, useRef } from 'react';
import { GetCMSContent } from 'aldoo-ra/CMS/api';
import { cleanDuplicateClasses } from './style-plugins/index.js';
import VariantPreview from './VariantEditorDialog/components/VariantPreview.jsx';
import { Icons } from './Icon.jsx';
import VariantSelector from './VariantSelector.jsx';

const VariantPopup = ({ controller }) => {
    const {
        x,
        y,
        componentType,
        onClose,
        onApply,
        onPreview,
        onRestore,
        previewNode,
        activeBreakpoint,
        onVariantChange,
        onDisabledChange,
        appliedVariant
    } = controller;
    const [variants, setVariants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCollectionId, setSelectedCollectionId] = useState(null);
    const [search, setSearch] = useState('');
    const [hoveredVariant, setHoveredVariant] = useState(null);
    const [position, setPosition] = useState({ x, y });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const popupRef = useRef(null);

    // Handle hover leave - restore original variant
    useEffect(() => {
        if (hoveredVariant === null && onRestore) {
            onRestore();
        }
    }, [hoveredVariant, onRestore]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (popupRef.current && !popupRef.current.contains(e.target)) {
                // Restore original variant on close
                if (onRestore) {
                    onRestore();
                }
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose, onRestore]);

    useEffect(() => {
        loadVariants();
    }, [componentType]);

    // Check if mouse is already hovering over a variant on mount
    useEffect(() => {
        const checkInitialHover = (e) => {
            if (!popupRef.current) return;
            const variantElements = popupRef.current.querySelectorAll('[data-variant-key]');
            variantElements.forEach(el => {
                const rect = el.getBoundingClientRect();
                if (
                    e.clientX >= rect.left &&
                    e.clientX <= rect.right &&
                    e.clientY >= rect.top &&
                    e.clientY <= rect.bottom
                ) {
                    const variantKey = el.getAttribute('data-variant-key');
                    setHoveredVariant(variantKey);
                    if (onPreview) {
                        onPreview(variantKey);
                    }
                }
            });
        };

        // Small delay to ensure DOM is ready
        const timer = setTimeout(() => {
            const handleMouseMove = (e) => {
                checkInitialHover(e);
                document.removeEventListener('mousemove', handleMouseMove);
            };
            document.addEventListener('mousemove', handleMouseMove);
        }, 50);

        return () => clearTimeout(timer);
    }, [onPreview]);

    // Handle dragging
    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e) => {
            setPosition({
                x: e.clientX - dragOffset.x,
                y: e.clientY - dragOffset.y
            });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset]);

    const handleDragStart = (e) => {
        if (popupRef.current) {
            const rect = popupRef.current.getBoundingClientRect();
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
            setIsDragging(true);
        }
    };

    const loadVariants = async () => {
        setLoading(true);
        try {
            const res = await GetCMSContent({
                match: {
                    'content.type': 'StyleVariant',
                    'content.component': { $in: [componentType, 'Typography'] }
                },
                itemsPerPage: 1000
            });
            if (res && res.data && Array.isArray(res.data)) {
                 const flatVariants = res.data.map(item => ({
                     ...item.content,
                     _id: item._id,
                     styles: Object.entries(item.content.styles || {}).map(([key, value]) => {
                         const cleanedValue = cleanDuplicateClasses(value);
                         return { key, value: cleanedValue };
                     })
                 }));
                 setVariants(flatVariants);
                 if (flatVariants.length > 0) {
                     setSelectedCollectionId(flatVariants[0]._id);
                 }
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const selectedCollection = variants.find(v => v._id === selectedCollectionId);

    // Position logic to ensure it stays in viewport
    const POPUP_WIDTH = 600;
    const POPUP_HEIGHT = 500;
    const PADDING = 16;

    let finalX = position.x;
    let finalY = position.y;

    if (typeof window !== 'undefined') {
        // Horizontal adjustment
        if (finalX + POPUP_WIDTH > window.innerWidth) {
            finalX = window.innerWidth - POPUP_WIDTH - PADDING;
        }
        if (finalX < PADDING) finalX = PADDING;

        // Vertical adjustment
        if (finalY + POPUP_HEIGHT > window.innerHeight) {
            finalY = window.innerHeight - POPUP_HEIGHT - PADDING;
        }
        if (finalY < PADDING) finalY = PADDING;
    }

    const style = {
        position: 'fixed',
        left: finalX,
        top: finalY,
        width: `${POPUP_WIDTH}px`,
        height: `${POPUP_HEIGHT}px`,
        zIndex: 10000,
    };

    return (
        <div
            ref={popupRef}
            className="bg-white border border-slate-300 rounded shadow-2xl flex flex-col overflow-hidden animate-fade-in"
            style={style}
        >
            <div
                className="p-2 border-b border-slate-200 bg-slate-50 flex justify-between items-center shrink-0 cursor-move"
                onMouseDown={handleDragStart}
            >
                <span className="text-xs font-bold text-gray-700 uppercase">Apply Variant</span>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <Icons.Close className="w-4 h-4" />
                </button>
            </div>

            <div className="flex flex-1 min-h-0">
                {/* Left Column: Applied Variants & Collections */}
                <div className="w-1/3 border-r border-slate-200 flex flex-col bg-slate-50">
                    {/* Top Section: Applied Variants */}
                    <div className="flex flex-col border-b border-slate-200" style={{ maxHeight: '40%' }}>
                        <div className="p-2 border-b border-slate-200 bg-slate-100">
                            <div className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Applied</div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-2 min-h-0">
                            <VariantSelector
                                value={appliedVariant || ''}
                                onChange={(newVariantString) => {
                                    if (onVariantChange) {
                                        onVariantChange(newVariantString);
                                    }
                                }}
                                disabledList={previewNode?.props?.disabledVariants || []}
                                onUpdateDisabled={(newDisabled) => {
                                    if (onDisabledChange) {
                                        onDisabledChange(newDisabled);
                                    }
                                }}
                                onVariantEdit={null}
                                componentType={componentType}
                                label=""
                                hideAddButton={true}
                            />
                        </div>
                    </div>

                    {/* Bottom Section: Collections */}
                    <div className="flex-1 flex flex-col min-h-0">
                        <div className="p-2 border-b border-slate-200">
                            <input
                                type="text"
                                placeholder="Search collections..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full text-xs p-1 border rounded"
                            />
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {loading ? (
                                <div className="p-4 text-center text-xs text-gray-400">Loading...</div>
                            ) : (
                                variants
                                    .filter(v => v.name.toLowerCase().includes(search.toLowerCase()))
                                    .map(v => (
                                        <div
                                            key={v._id}
                                            onClick={() => setSelectedCollectionId(v._id)}
                                            className={`px-3 py-2 text-xs cursor-pointer truncate ${
                                                selectedCollectionId === v._id
                                                    ? 'bg-blue-100 text-blue-700 font-semibold border-r-2 border-blue-500'
                                                    : 'text-gray-600 hover:bg-slate-100'
                                            }`}
                                        >
                                            {v.name}
                                        </div>
                                    ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column: Variants Preview List */}
                <div className="w-2/3 flex flex-col bg-white">
                    <div className="flex-1 overflow-y-auto p-2 space-y-2">
                        {selectedCollection ? (
                            selectedCollection.styles.map(style => (
                                <div
                                    key={style.key}
                                    data-variant-key={style.key}
                                    onClick={() => {
                                        onApply(style.key, selectedCollection.component);
                                    }}
                                    onMouseEnter={() => {
                                        setHoveredVariant(style.key);
                                        if (onPreview) {
                                            onPreview(style.key);
                                        }
                                    }}
                                    onMouseLeave={() => {
                                        setHoveredVariant(null);
                                    }}
                                    className="group cursor-pointer border border-slate-100 hover:border-blue-400 hover:shadow-sm rounded transition-all p-1"
                                >
                                    <div className="mb-0.5 flex justify-between items-center px-1">
                                        <span className="text-[10px] font-bold text-gray-500 group-hover:text-blue-600 transition-colors uppercase tracking-tight">
                                            {style.key}
                                        </span>
                                    </div>
                                    <div className="rounded overflow-hidden bg-white h-[60px] flex items-center justify-center pointer-events-none">
                                        <VariantPreview
                                            variant={selectedCollection}
                                            activeKey={style.key}
                                            disabledPreviewKeys={[]}
                                            componentType={componentType}
                                            previewNode={previewNode}
                                            activeBreakpoint={activeBreakpoint}
                                            compact={true}
                                        />
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="h-full flex items-center justify-center text-gray-400 text-xs italic">
                                Select a collection
                            </div>
                        )}
                        {selectedCollection && selectedCollection.styles.length === 0 && (
                            <div className="h-full flex items-center justify-center text-gray-400 text-xs italic">
                                No variants in this collection
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VariantPopup;
